<template>
  <div
    class="absolute left-0 z-1 mb-5 flex w-90 flex-col space-y-5 rounded-sm border border-black/30 bg-white p-5 shadow-lg"
    :class="modalPositionClass"
  >
    <div @click="emit('close')" class="w-full cursor-pointer text-right">
      <icon-base icon="x" />
    </div>
    <div v-if="isCustom" class="flex flex-col space-y-5">
      <div class="flex space-x-4">
        <icon-base
          :icon="IconBack"
          class="cursor-pointer"
          height="20"
          width="20"
          @click.stop="reset(false)"
        />
        <lf-h4>{{ $t("SCORECARDS.CREATE_CUSTOM_STATUS") }}</lf-h4>
      </div>
      <lf-input
        :placeholder="$t('SCORECARDS.STATUS_NAME')"
        name="customStatusName"
        class="-mb-0"
        @keyReleased="handleCustomStatusName"
      />
    </div>

    <status-dropdown
      v-else
      v-model="status"
      class="z-10"
      @custom-selected="handleCustomSelected"
    />

    <lf-textarea
      :placeholder="$t('COMMON.DESCRIPTION')"
      :value="description"
      rows="4"
      name="customStatusDescription"
      noresize
      :character-limit="TEXT_CHARACTER_LIMIT_MEDIUM"
      @keyReleased="handleDescription"
    />

    <div v-if="isCustom" class="space-y-2">
      <span>
        {{ $t("COMMON.COLOR") }}
      </span>
      <div class="grid grid-cols-9 gap-2">
        <div
          v-for="color in hexColors"
          :key="color"
          class="h-4 w-4 cursor-pointer rounded-full"
          :class="{
            'ring-2 ring-gray-400 ring-offset-2': color === customColor,
          }"
          :style="{
            backgroundColor: color,
          }"
          @click.stop="customColor = color"
        />
      </div>
    </div>

    <points-select v-model="pointsRange" :min="0" :max="currentPoints" />

    <div class="flex items-center space-x-2">
      <input
        id="isDead"
        v-model="isDead"
        class="cursor-pointer rounded-sm border-2-5 border-gray-300"
        type="checkbox"
      />
      <label for="isDead" class="cursor-pointer">
        {{ $t("SCORECARDS.DEAD_STATUS") }}
      </label>
    </div>
    <div class="flex justify-end">
      <primary-button
        class="uppercase"
        :disabled="!currentPoints"
        @click="handleAdd"
      >
        {{ $t("COMMON.ADD") }}
      </primary-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";

import { COLORS, TEXT_CHARACTER_LIMIT_MEDIUM } from "@/helpers/constants";
import { makeRandomString, rgbToHex } from "@/helpers/formatting";

import IconBack from "@/components/icons/IconBack.vue";
import PointsSelect from "@/components/scorecards/PointsSelect.vue";
import StatusDropdown from "@/components/scorecards/StatusDropdown.vue";

import type {
  IScorecardStatus,
  IStatusDropdownItem,
} from "@/models/scorecards";
import type { PropType } from "vue";

const emit = defineEmits<{
  (eventName: "update", statuses: IScorecardStatus): void;
  (eventName: "close"): void;
}>();

const props = defineProps({
  currentPoints: {
    type: Number,
    required: true,
  },
  statusToEdit: {
    type: Object as PropType<IScorecardStatus | null>,
    default: null,
  },
});

const { getters } = useStore();

const impersonatedUser = computed(() => getters["auth/impersonating"]);

const modalPositionClass = computed(() =>
  impersonatedUser.value ? "-bottom-full" : "bottom-full"
);

const getInitialRange = computed<[number, number]>(() =>
  props.statusToEdit
    ? [props.statusToEdit.points_from || 0, props.statusToEdit.points_to || 0]
    : [0, props.currentPoints]
);
const hexColors = COLORS.map(rgbToHex);

const isCustom = ref(false);
const description = ref(props.statusToEdit?.description || "");
const status = ref<IStatusDropdownItem | null>(
  props.statusToEdit
    ? {
        label: props.statusToEdit.name,
        color: props.statusToEdit.color,
      }
    : null
);
const pointsRange = ref(getInitialRange.value);
const customColor = ref("");
const customName = ref("");
const isDead = ref(props.statusToEdit?.is_dead || false);

const handleCustomSelected = () => {
  status.value = null;
  isCustom.value = true;
};

const reset = (close = true) => {
  if (close) {
    emit("close");
  }
  isCustom.value = false;
  customColor.value = "";
  customName.value = "";
  description.value = "";
  status.value = null;
  pointsRange.value = getInitialRange.value;
  isDead.value = false;
};

const handleCustomStatusName = (val: string) => {
  customName.value = val;
};

const handleDescription = (val: string) => {
  description.value = val;
};

const handleAdd = () => {
  const name = isCustom.value ? customName.value : status.value?.label || "";
  const color = isCustom.value ? customColor.value : status.value?.color || "";
  const [points_from, points_to] = pointsRange.value;

  if (
    !props.currentPoints ||
    !name ||
    !color ||
    !points_to ||
    (points_from !== 0 && !points_from)
  ) {
    return;
  }

  const newStatus: IScorecardStatus = {
    id: props.statusToEdit?.id || makeRandomString(),
    name,
    color,
    is_dead: isDead.value,
    description: description.value,
    points_from,
    points_to,
  };

  emit("update", newStatus);
  reset();
};

watch(
  () => props.currentPoints,
  () => {
    pointsRange.value = getInitialRange.value;
  }
);
</script>
